// client/src/components/admin/SuperAdminDashboard.jsx
// Platform-level admin dashboard — restricted to SE3D / On-Screen tenants

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getJson, postJson, patchJson, delJson } from '../../services/http';
import AppFooter from '../AppFooter';
import { FaHome, FaSync } from 'react-icons/fa';
import SuperAdminTaxTab from './SuperAdminTaxTab';
import SuperAdminNonprofitTab from './SuperAdminNonprofitTab';
import './SuperAdminDashboard.css';

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'tracking', label: 'Tracking' },
  { key: 'transactions', label: 'Transactions' },
  { key: 'errors', label: 'Errors' },
  { key: 'tenants', label: 'Tenants' },
  { key: 'broadcast', label: 'Messages' },
  { key: 'contact', label: 'Contact' },
  { key: 'tax', label: 'Tax' },
  { key: 'nonprofits', label: 'Nonprofits' },
];

export default function SuperAdminDashboard() {
  const { tenant } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [authorized, setAuthorized] = useState(null); // null = loading
  const [error, setError] = useState(null);

  // Check super-admin access on mount
  useEffect(() => {
    getJson('/api/super-admin/check-access')
      .then(() => setAuthorized(true))
      .catch(() => {
        setAuthorized(false);
        setError('You do not have access to the super admin dashboard.');
      });
  }, []);

  if (authorized === null) return <div className="sa-loading">Checking access...</div>;
  if (!authorized) return <div className="super-admin"><p style={{ color: 'red' }}>{error}</p></div>;

  return (
    <div className="super-admin">
      <h2>Platform Admin</h2>

      <div className="sa-tabs">
        {TABS.map(t => (
          <button
            key={t.key}
            className={`sa-tab ${tab === t.key ? 'active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab />}
      {tab === 'tracking' && <TrackingTab />}
      {tab === 'transactions' && <TransactionsTab />}
      {tab === 'errors' && <ErrorsTab />}
      {tab === 'tenants' && <TenantsTab />}
      {tab === 'broadcast' && <BroadcastTab />}
      {tab === 'contact' && <ContactTab />}
      {tab === 'tax' && <SuperAdminTaxTab />}
      {tab === 'nonprofits' && <SuperAdminNonprofitTab />}

      <AppFooter
        buttons={[
          { icon: <FaHome />, label: 'Home', onClick: () => navigate(`/${tenant}/dashboard`) },
        ]}
      />
    </div>
  );
}

// ============================================================================
// Overview Tab — platform metrics
// ============================================================================
function OverviewTab() {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    getJson('/api/super-admin/metrics')
      .then(d => setMetrics(d.metrics))
      .catch(err => console.error('Metrics load error:', err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="sa-loading">Loading metrics...</div>;
  if (!metrics) return <p>Failed to load metrics.</p>;

  const maxSignup = Math.max(...metrics.signupTrend.map(s => s.count), 1);

  return (
    <div>
      <div className="sa-section-header">
        <h3>Platform Metrics</h3>
        <button className="sa-btn" onClick={load}><FaSync /> Refresh</button>
      </div>

      <div className="sa-metrics-grid">
        <MetricCard value={metrics.totalTenants} label="Total Tenants" />
        <MetricCard value={metrics.totalUsers} label="Total Users" />
        <MetricCard value={metrics.totalEvents} label="Total Events" />
        <MetricCard value={metrics.totalPosts} label="Total Posts" />
        <MetricCard value={metrics.totalClicks} label="Total Clicks" />
        <MetricCard value={metrics.newTenantsToday} label="New Tenants Today" />
        <MetricCard value={metrics.newUsersToday} label="New Users Today" />
      </div>

      <h3 style={{ fontSize: '1rem', marginBottom: 4 }}>Activation Funnel</h3>
      <div className="sa-metrics-grid">
        <MetricCard value={metrics.totalTenants} label="Account Created" />
        <MetricCard value={metrics.tenantsWithEvents} label="First Event Created" />
        <MetricCard value={metrics.tenantsWithPosts} label="First Post Scheduled" />
      </div>

      <h3 style={{ fontSize: '1rem', margin: '16px 0 4px' }}>Tenant Signups (30 days)</h3>
      {metrics.signupTrend.length > 0 ? (
        <div className="sa-bar-chart">
          {metrics.signupTrend.map((s, i) => (
            <div
              key={i}
              className="sa-bar"
              style={{ height: `${(s.count / maxSignup) * 100}%` }}
              title={`${s.date}: ${s.count}`}
            />
          ))}
        </div>
      ) : (
        <p style={{ color: '#888', fontSize: '0.85rem' }}>No signups in the last 30 days.</p>
      )}
    </div>
  );
}

function MetricCard({ value, label }) {
  return (
    <div className="sa-metric-card">
      <div className="value">{typeof value === 'number' ? value.toLocaleString() : value}</div>
      <div className="label">{label}</div>
    </div>
  );
}

// ============================================================================
// Tracking Tab — click tracking stats
// ============================================================================
function TrackingTab() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    getJson(`/api/super-admin/tracking?days=${days}`)
      .then(d => setData(d))
      .catch(err => console.error('Tracking load error:', err))
      .finally(() => setLoading(false));
  }, [days]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="sa-loading">Loading tracking data...</div>;
  if (!data) return <p>Failed to load tracking data.</p>;

  const maxDaily = Math.max(...data.daily.map(d => d.clicks), 1);

  return (
    <div>
      <div className="sa-section-header">
        <h3>Click Tracking</h3>
        <div className="sa-filters">
          <select value={days} onChange={e => setDays(Number(e.target.value))}>
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
          </select>
          <button className="sa-btn" onClick={load}><FaSync /></button>
        </div>
      </div>

      <div className="sa-metrics-grid">
        <MetricCard value={data.totals.totalClicks} label="Total Clicks" />
        <MetricCard value={data.totals.tenantsWithClicks} label="Tenants with Clicks" />
        <MetricCard value={data.totals.uniqueLinks} label="Unique Links" />
      </div>

      <h3 style={{ fontSize: '1rem', margin: '12px 0 4px' }}>Daily Clicks</h3>
      {data.daily.length > 0 ? (
        <div className="sa-bar-chart">
          {data.daily.map((d, i) => (
            <div
              key={i}
              className="sa-bar"
              style={{ height: `${(d.clicks / maxDaily) * 100}%` }}
              title={`${d.date}: ${d.clicks} clicks`}
            />
          ))}
        </div>
      ) : (
        <p style={{ color: '#888', fontSize: '0.85rem' }}>No clicks in this period.</p>
      )}

      <h3 style={{ fontSize: '1rem', margin: '16px 0 8px' }}>Clicks by Tenant</h3>
      <div className="sa-table-wrap">
        <table className="sa-table">
          <thead>
            <tr>
              <th>Tenant</th>
              <th>Clicks</th>
            </tr>
          </thead>
          <tbody>
            {data.byTenant.map((r, i) => (
              <tr key={i}>
                <td>{r.companyName || `Tenant #${r.tenantId}`}</td>
                <td>{r.clicks.toLocaleString()}</td>
              </tr>
            ))}
            {data.byTenant.length === 0 && (
              <tr><td colSpan={2} style={{ textAlign: 'center', color: '#888' }}>No data</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <h3 style={{ fontSize: '1rem', margin: '16px 0 8px' }}>Top Links</h3>
      <div className="sa-table-wrap">
        <table className="sa-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Destination</th>
              <th>Clicks</th>
            </tr>
          </thead>
          <tbody>
            {data.topLinks.map((l, i) => (
              <tr key={i}>
                <td><code>{l.trackingCode}</code></td>
                <td className="sa-truncate" title={l.destination}>{l.destination}</td>
                <td>{l.clicks.toLocaleString()}</td>
              </tr>
            ))}
            {data.topLinks.length === 0 && (
              <tr><td colSpan={3} style={{ textAlign: 'center', color: '#888' }}>No data</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <h3 style={{ fontSize: '1rem', margin: '16px 0 4px' }}>Site Visits</h3>
      <div className="sa-metrics-grid">
        <MetricCard value={data.siteVisits.totals.totalVisits} label="Site Visits" />
        <MetricCard value={data.siteVisits.totals.uniquePages} label="Pages Tracked" />
      </div>

      <div className="sa-table-wrap">
        <table className="sa-table">
          <thead>
            <tr>
              <th>Page</th>
              <th>Visits</th>
            </tr>
          </thead>
          <tbody>
            {data.siteVisits.byPath.map((r, i) => (
              <tr key={i}>
                <td><code>{r.path}</code></td>
                <td>{r.visits.toLocaleString()}</td>
              </tr>
            ))}
            {data.siteVisits.byPath.length === 0 && (
              <tr><td colSpan={2} style={{ textAlign: 'center', color: '#888' }}>No visits in this period</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================================
// Transactions Tab — cross-tenant log of business actions (t_transaction).
// For metrics analysis and tenant troubleshooting. Backend:
// superAdminRoutes.js → /api/super-admin/transactions[/summary|/filters|/:id]
// ============================================================================
const TXN_EMPTY_FILTERS = {
  tenantId: '', type: '', component: '', username: '', search: '', dateFrom: '', dateTo: '',
};

function txnQueryString(filters, extra = {}) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
  Object.entries(extra).forEach(([k, v]) => { if (v != null) params.set(k, v); });
  return params.toString();
}

function TransactionsTab() {
  const [filters, setFilters] = useState(TXN_EMPTY_FILTERS);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 1 });
  const [summary, setSummary] = useState(null);
  const [options, setOptions] = useState({ types: [], components: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  // Filter option lists (types + components in use) — loaded once.
  useEffect(() => {
    getJson('/api/super-admin/transactions/filters')
      .then(d => setOptions({ types: d.types || [], components: d.components || [] }))
      .catch(() => {});
  }, []);

  const loadPage = useCallback((page) => {
    setLoading(true);
    setError(null);
    getJson(`/api/super-admin/transactions?${txnQueryString(filters, { page, limit: 50 })}`)
      .then(d => {
        setRows(d.data || []);
        setPagination(d.pagination || { page: 1, limit: 50, total: 0, totalPages: 1 });
      })
      .catch(() => setError('Failed to load transactions.'))
      .finally(() => setLoading(false));
  }, [filters]);

  const loadSummary = useCallback(() => {
    getJson(`/api/super-admin/transactions/summary?${txnQueryString(filters)}`)
      .then(setSummary)
      .catch(() => setSummary(null));
  }, [filters]);

  // Debounce filter changes so typing in the text fields doesn't spam the API.
  useEffect(() => {
    const t = setTimeout(() => { loadPage(1); loadSummary(); }, 350);
    return () => clearTimeout(t);
  }, [loadPage, loadSummary]);

  const updateFilter = (key, value) => {
    setExpandedId(null);
    setFilters(f => ({ ...f, [key]: value }));
  };

  const exportCsv = () => {
    window.open(
      `/api/super-admin/transactions?${txnQueryString(filters, { format: 'csv', limit: 5000 })}`,
      '_blank'
    );
  };

  const maxDaily = Math.max(...(summary?.daily || []).map(d => d.actionCount), 1);

  return (
    <div>
      <div className="sa-section-header">
        <h3>Transactions</h3>
        <button className="sa-btn" onClick={() => { loadPage(pagination.page); loadSummary(); }}>
          <FaSync /> Refresh
        </button>
      </div>

      <div className="sa-panel">
        Every logged action across every tenant — logins, event and post activity, billing,
        integrations, member and user changes, and more. Filter by tenant to troubleshoot a
        specific account, or by type and date range to analyse platform activity. Click a row
        for its full context and metadata.
      </div>

      {summary && (
        <>
          <div className="sa-metrics-grid">
            <MetricCard value={summary.totals.totalActions} label="Actions (filtered)" />
            <MetricCard value={summary.totals.distinctTenants} label="Tenants" />
            <MetricCard value={summary.totals.distinctUsers} label="Users" />
            <MetricCard value={summary.totals.todayActions} label="Today" />
          </div>
          {summary.daily.length > 0 && (
            <>
              <h3 style={{ fontSize: '1rem', margin: '4px 0' }}>Daily Activity</h3>
              <div className="sa-bar-chart">
                {summary.daily.map((d, i) => (
                  <div
                    key={i}
                    className="sa-bar"
                    style={{ height: `${(d.actionCount / maxDaily) * 100}%` }}
                    title={`${d.date}: ${d.actionCount}`}
                  />
                ))}
              </div>
            </>
          )}
        </>
      )}

      <div className="sa-filters">
        <input
          placeholder="Tenant ID"
          value={filters.tenantId}
          inputMode="numeric"
          onChange={e => updateFilter('tenantId', e.target.value.replace(/\D/g, ''))}
          style={{ width: 90 }}
        />
        <select value={filters.type} onChange={e => updateFilter('type', e.target.value)}>
          <option value="">All types</option>
          {options.types.map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
        </select>
        <select value={filters.component} onChange={e => updateFilter('component', e.target.value)}>
          <option value="">All components</option>
          {options.components.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <input
          placeholder="Username"
          value={filters.username}
          onChange={e => updateFilter('username', e.target.value)}
        />
        <input type="date" value={filters.dateFrom} onChange={e => updateFilter('dateFrom', e.target.value)} />
        <input type="date" value={filters.dateTo} onChange={e => updateFilter('dateTo', e.target.value)} />
        <input
          placeholder="Search action, entity, details"
          value={filters.search}
          onChange={e => updateFilter('search', e.target.value)}
          style={{ minWidth: 200 }}
        />
        <button className="sa-btn" onClick={() => setFilters(TXN_EMPTY_FILTERS)}>Clear</button>
        <button className="sa-btn primary" onClick={exportCsv}>Export CSV</button>
      </div>

      {error && <div className="sa-note error">{error}</div>}

      <div className="sa-table-wrap">
        <table className="sa-table">
          <thead>
            <tr>
              <th>Time</th><th>Tenant</th><th>User</th><th>Type</th><th>Component</th><th>Entity</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', color: '#888' }}>Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', color: '#888' }}>No transactions match these filters.</td></tr>
            ) : rows.map(r => (
              <React.Fragment key={r.id}>
                <tr
                  className="clickable"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}
                >
                  <td style={{ whiteSpace: 'nowrap' }}>{new Date(r.createdOn).toLocaleString()}</td>
                  <td>{r.companyName || `#${r.tenantId}`}</td>
                  <td>{r.username || '—'}</td>
                  <td><code>{r.type}</code></td>
                  <td>{r.component}</td>
                  <td>{r.entityName || (r.entityId ? `#${r.entityId}` : '—')}</td>
                </tr>
                {expandedId === r.id && (
                  <tr>
                    <td colSpan={6} style={{ padding: 0 }}>
                      <div className="sa-error-detail">
                        <strong>Transaction #{r.id}</strong>{'\n'}
                        <strong>Type:</strong> {r.type}{r.typeDescription ? ` — ${r.typeDescription}` : ''}{'\n'}
                        <strong>Tenant:</strong> {r.companyName || '—'} (#{r.tenantId}){'\n'}
                        <strong>User:</strong> {r.username || '—'}{'\n'}
                        <strong>Component:</strong> {r.component}{'\n'}
                        <strong>Event ID:</strong> {r.eventId ?? '—'}{'\n'}
                        <strong>Entity:</strong> {r.entityName || '—'}{r.entityId ? ` (#${r.entityId})` : ''}{'\n'}
                        <strong>Time:</strong> {new Date(r.createdOn).toLocaleString()}{'\n\n'}
                        <strong>Details:</strong>{'\n'}
                        {r.details ? JSON.stringify(r.details, null, 2) : 'none'}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div className="sa-panel" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <span>{pagination.total.toLocaleString()} total transactions</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button className="sa-btn" disabled={pagination.page <= 1 || loading} onClick={() => loadPage(pagination.page - 1)}>Prev</button>
          <span>Page {pagination.page} of {pagination.totalPages}</span>
          <button className="sa-btn" disabled={pagination.page >= pagination.totalPages || loading} onClick={() => loadPage(pagination.page + 1)}>Next</button>
        </div>
      </div>

      {summary && summary.byType.length > 0 && (
        <>
          <h3 style={{ fontSize: '1rem', margin: '16px 0 8px' }}>By Type</h3>
          <div className="sa-table-wrap">
            <table className="sa-table">
              <thead><tr><th>Type</th><th>Actions</th></tr></thead>
              <tbody>
                {summary.byType.map(r => (
                  <tr key={r.type}><td><code>{r.type}</code></td><td>{r.actionCount.toLocaleString()}</td></tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3 style={{ fontSize: '1rem', margin: '16px 0 8px' }}>By Tenant</h3>
          <div className="sa-table-wrap">
            <table className="sa-table">
              <thead><tr><th>Tenant</th><th>Actions</th></tr></thead>
              <tbody>
                {summary.byTenant.map(r => (
                  <tr key={r.tenantId}><td>{r.companyName || `#${r.tenantId}`}</td><td>{r.actionCount.toLocaleString()}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Errors Tab — distinct errors grouped by type+message, with counts & bulk resolve
// ============================================================================
function ErrorsTab() {
  const [errors, setErrors] = useState([]);
  const [counts, setCounts] = useState({ total: 0, active: 0, resolved: 0 });
  const [statusFilter, setStatusFilter] = useState('active');
  const [expandedKey, setExpandedKey] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    const qs = statusFilter ? `?status=${statusFilter}` : '';
    getJson(`/api/super-admin/errors${qs}`)
      .then(d => {
        setErrors(d.errors || []);
        setCounts(d.counts || { total: 0, active: 0, resolved: 0 });
        setSelected(new Set());
      })
      .catch(err => console.error('Errors load error:', err))
      .finally(() => setLoading(false));
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  // Unique key for each grouped error row
  const errKey = (e) => `${e.type}::${e.message}`;

  const toggleSelect = (e) => {
    setSelected(prev => {
      const next = new Set(prev);
      const key = errKey(e);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === errors.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(errors.map(errKey)));
    }
  };

  const bulkResolve = async () => {
    const ids = errors
      .filter(e => selected.has(errKey(e)))
      .flatMap(e => e.ids);
    if (ids.length === 0) return;
    try {
      await postJson('/api/super-admin/errors/bulk-status', { ids, status: 'resolved' });
      load();
    } catch (err) {
      console.error('Bulk resolve error:', err);
    }
  };

  const bulkReopen = async () => {
    const ids = errors
      .filter(e => selected.has(errKey(e)))
      .flatMap(e => e.ids);
    if (ids.length === 0) return;
    try {
      await postJson('/api/super-admin/errors/bulk-status', { ids, status: 'active' });
      load();
    } catch (err) {
      console.error('Bulk reopen error:', err);
    }
  };

  return (
    <div>
      <div className="sa-section-header">
        <h3>Error Log</h3>
        <button className="sa-btn" onClick={load}><FaSync /> Refresh</button>
      </div>

      <div className="sa-metrics-grid" style={{ marginBottom: 12 }}>
        <MetricCard value={counts.active} label="Active" />
        <MetricCard value={counts.resolved} label="Resolved" />
        <MetricCard value={counts.total} label="Total" />
      </div>

      <div className="sa-filters" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All</option>
          <option value="active">Active</option>
          <option value="resolved">Resolved</option>
        </select>
        {errors.length > 0 && (
          <>
            <button className="sa-btn" onClick={selectAll} style={{ marginLeft: 8 }}>
              {selected.size === errors.length ? 'Deselect All' : 'Select All'}
            </button>
            {selected.size > 0 && (
              <>
                <button className="sa-btn success" onClick={bulkResolve}>
                  Resolve Selected ({selected.size})
                </button>
                <button className="sa-btn danger" onClick={bulkReopen}>
                  Reopen Selected ({selected.size})
                </button>
              </>
            )}
          </>
        )}
      </div>

      {loading ? (
        <div className="sa-loading">Loading errors...</div>
      ) : errors.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#888', padding: 20 }}>No errors found</div>
      ) : (
        <div className="sa-card-list">
          {errors.map(e => {
            const key = errKey(e);
            const isSelected = selected.has(key);
            const isExpanded = expandedKey === key;
            return (
              <div
                key={key}
                className={`sa-card${isSelected ? ' sa-card-selected' : ''}`}
                onClick={() => setExpandedKey(isExpanded ? null : key)}
              >
                <div className="sa-card-row" style={{ alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(ev) => { ev.stopPropagation(); toggleSelect(e); }}
                    onClick={(ev) => ev.stopPropagation()}
                    style={{ marginRight: 8, width: 18, height: 18 }}
                  />
                  <span className="sa-card-label">Type</span>
                  <code>{e.type}</code>
                  <span className="sa-error-count-badge">{e.errorCount}</span>
                </div>
                <div className="sa-card-row">
                  <span className="sa-card-label">Message</span>
                  <span className="sa-card-value-wrap">{e.message || '—'}</span>
                </div>
                <div className="sa-card-row">
                  <span className="sa-card-label">Latest</span>
                  <span>{e.latestAt ? new Date(e.latestAt).toLocaleString() : '—'}</span>
                </div>
                <div className="sa-card-row">
                  <span className="sa-card-label">Tenant</span>
                  <span>{e.tenantName || (e.tenantId ? `#${e.tenantId}` : '—')}</span>
                </div>
                <div className="sa-card-row">
                  <span className="sa-card-label">Status</span>
                  <span className={`sa-badge ${e.status}`}>{e.status}</span>
                </div>
                {isExpanded && (
                  <div className="sa-error-detail">
                    <strong>Occurrences:</strong> {e.errorCount}{'\n'}
                    <strong>First seen:</strong> {e.earliestAt ? new Date(e.earliestAt).toLocaleString() : '—'}{'\n'}
                    <strong>Last seen:</strong> {e.latestAt ? new Date(e.latestAt).toLocaleString() : '—'}{'\n'}
                    <strong>URL:</strong> {e.url || '—'}{'\n'}
                    <strong>User Agent:</strong> {e.userAgent || '—'}{'\n'}
                    <strong>Platform:</strong> {e.platform || '—'}{'\n\n'}
                    <strong>Stack Trace:</strong>{'\n'}{e.stack || 'No stack trace'}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Tenants Tab — list, enable/disable
// ============================================================================
function TenantsTab() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    getJson('/api/super-admin/tenants')
      .then(d => setTenants(d.tenants || []))
      .catch(err => console.error('Tenants load error:', err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleTenant = async (id, currentActive) => {
    if (!window.confirm(`${currentActive ? 'Disable' : 'Enable'} this tenant?`)) return;
    try {
      await patchJson(`/api/super-admin/tenants/${id}/status`, { active: !currentActive });
      load();
    } catch (err) {
      console.error('Tenant status toggle error:', err);
    }
  };

  const toggleBilling = async (id, currentEnabled) => {
    const verb = currentEnabled ? 'Disable' : 'Enable';
    if (!window.confirm(`${verb} billing enforcement for this tenant? When disabled, all post/integration/user limits are skipped for testing.`)) return;
    try {
      await patchJson(`/api/super-admin/tenants/${id}/billing-enabled`, { enabled: !currentEnabled });
      load();
    } catch (err) {
      console.error('Tenant billing toggle error:', err);
    }
  };

  const filtered = tenants.filter(t =>
    !search || t.companyName.toLowerCase().includes(search.toLowerCase()) ||
    t.email.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div className="sa-section-header">
        <h3>Tenants ({tenants.length})</h3>
        <button className="sa-btn" onClick={load}><FaSync /> Refresh</button>
      </div>

      <div className="sa-filters">
        <input
          type="text"
          placeholder="Search tenants..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="sa-loading">Loading tenants...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#888', padding: 20 }}>No tenants found</div>
      ) : (
        <div className="sa-card-list">
          {filtered.map(t => (
            <div key={t.id} className="sa-card">
              <div className="sa-card-row">
                <span className="sa-card-label">ID</span>
                <span>{t.id}</span>
              </div>
              <div className="sa-card-row">
                <span className="sa-card-label">Company</span>
                <span>{t.companyName}</span>
              </div>
              <div className="sa-card-row">
                <span className="sa-card-label">Email</span>
                <span className="sa-card-value-wrap">{t.email}</span>
              </div>
              <div className="sa-card-row sa-card-row-stats">
                <span><strong>{t.userCount}</strong> Users</span>
                <span><strong>{t.eventCount}</strong> Events</span>
                <span><strong>{t.postCount}</strong> Posts</span>
              </div>
              <div className="sa-card-row">
                <span className="sa-card-label">Status</span>
                <span className={`sa-badge ${t.active ? 'enabled' : 'disabled'}`}>
                  {t.active ? 'Active' : 'Disabled'}
                </span>
              </div>
              <div className="sa-card-row">
                <span className="sa-card-label">Billing</span>
                <span className={`sa-badge ${t.billingEnabled ? 'enabled' : 'disabled'}`}>
                  {t.billingEnabled ? 'Enforced' : 'Unrestricted'}
                </span>
              </div>
              <div className="sa-card-row">
                <span className="sa-card-label">Created</span>
                <span>{t.createdOn ? new Date(t.createdOn).toLocaleDateString() : '—'}</span>
              </div>
              <div className="sa-card-actions">
                <button
                  className={`sa-btn ${t.active ? 'danger' : 'success'}`}
                  onClick={() => toggleTenant(t.id, t.active)}
                >
                  {t.active ? 'Disable' : 'Enable'}
                </button>
                <button
                  className={`sa-btn ${t.billingEnabled ? 'success' : 'danger'}`}
                  onClick={() => toggleBilling(t.id, t.billingEnabled)}
                  title="Toggle billing/limit enforcement for this tenant"
                >
                  {t.billingEnabled ? 'Unrestrict' : 'Enforce Billing'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Broadcast Tab — send messages to tenants
// ============================================================================
function BroadcastTab() {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [targetType, setTargetType] = useState('all');
  const [selectedTenants, setSelectedTenants] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    getJson('/api/super-admin/tenants').then(d => setTenants(d.tenants || [])).catch(() => {});
    getJson('/api/super-admin/broadcasts').then(d => setBroadcasts(d.broadcasts || [])).catch(() => {});
  }, []);

  const toggleTenantSelect = (id) => {
    setSelectedTenants(prev =>
      prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]
    );
  };

  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) {
      setMessage({ type: 'error', text: 'Subject and message body are required.' });
      return;
    }
    if (targetType === 'specific' && selectedTenants.length === 0) {
      setMessage({ type: 'error', text: 'Select at least one tenant.' });
      return;
    }

    if (!window.confirm(`Send this message to ${targetType === 'all' ? 'ALL tenants' : `${selectedTenants.length} tenant(s)`}?`)) {
      return;
    }

    setSending(true);
    setMessage(null);
    try {
      const result = await postJson('/api/super-admin/broadcast', {
        subject: subject.trim(),
        body: body.trim(),
        targetType,
        targetTenantIds: targetType === 'specific' ? selectedTenants : undefined,
      });
      setMessage({
        type: 'success',
        text: `Message sent to ${result.recipientCount} tenant(s).`,
      });
      setSubject('');
      setBody('');
      setSelectedTenants([]);
      // Refresh broadcasts list
      getJson('/api/super-admin/broadcasts').then(d => setBroadcasts(d.broadcasts || [])).catch(() => {});
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to send message.' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <h3 style={{ fontSize: '1.1rem', marginBottom: 12 }}>Send Message</h3>

      {message && (
        <div style={{
          padding: '8px 12px',
          marginBottom: 12,
          borderRadius: 4,
          background: message.type === 'error' ? '#fff0f0' : '#f0fff0',
          color: message.type === 'error' ? '#c00' : '#070',
          fontSize: '0.85rem',
        }}>
          {message.text}
        </div>
      )}

      <div className="sa-broadcast-form">
        <div className="form-group">
          <label>Target</label>
          <select value={targetType} onChange={e => setTargetType(e.target.value)}>
            <option value="all">All Tenants</option>
            <option value="specific">Specific Tenants</option>
          </select>
        </div>

        {targetType === 'specific' && (
          <div className="form-group">
            <label>Select Tenants ({selectedTenants.length} selected)</label>
            <div className="sa-tenant-select">
              {tenants.map(t => (
                <label key={t.id}>
                  <input
                    type="checkbox"
                    checked={selectedTenants.includes(t.id)}
                    onChange={() => toggleTenantSelect(t.id)}
                  />
                  {t.companyName} ({t.email})
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="form-group">
          <label>Subject</label>
          <input
            type="text"
            value={subject}
            onChange={e => setSubject(e.target.value)}
            placeholder="Message subject..."
          />
        </div>

        <div className="form-group">
          <label>Message</label>
          <textarea
            value={body}
            onChange={e => setBody(e.target.value)}
            placeholder="Write your message..."
          />
        </div>

        <button
          className="sa-btn primary"
          onClick={handleSend}
          disabled={sending}
        >
          {sending ? 'Sending...' : 'Send Message'}
        </button>
      </div>

      <h3 style={{ fontSize: '1.1rem', margin: '20px 0 12px' }}>Sent Messages</h3>
      {broadcasts.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#888', padding: 20 }}>No messages sent yet</div>
      ) : (
        <div className="sa-card-list">
          {broadcasts.map(b => (
            <div key={b.id} className="sa-card" style={{ cursor: 'default' }}>
              <div className="sa-card-row">
                <span className="sa-card-label">Date</span>
                <span>{new Date(b.createdAt).toLocaleString()}</span>
              </div>
              <div className="sa-card-row">
                <span className="sa-card-label">Target</span>
                <span>{b.targetType === 'all' ? 'All' : `${(b.targetTenantIds || []).length} tenant(s)`}</span>
              </div>
              <div className="sa-card-row">
                <span className="sa-card-label">Subject</span>
                <span>{b.subject}</span>
              </div>
              <div style={{ fontSize: '0.82rem', color: '#555', marginTop: 4, wordBreak: 'break-word' }}>
                {b.body}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Contact — send email from info@on-screen.app.
//   Compose   — type a one-off message (support reply, general note, or a
//               quick outreach), pick a template, send now.
//   Campaigns — bulk cold-outreach with a prospect list, preview, queue,
//               and open/click/unsubscribe tracking.
// Backend: server/routes/outreachRoutes.js (/api/super-admin/outreach)
// ============================================================================

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const OUTREACH_BASE = '/api/super-admin/outreach';

// Turn a fetch/HTTP error into something a human can act on.
function describeError(err, fallback) {
  const status = err?.response?.status;
  const serverMsg = err?.response?.data?.error || err?.response?.data?.message;
  if (status === 404 || /404/.test(err?.message || '')) {
    return `${fallback}: the Contact API isn't responding (404). The server may need a restart to pick up the new routes.`;
  }
  if (status === 500) {
    return `${fallback}: the server hit an error. If you haven't run the outreach database migration yet (t_outreach_* / t_email_suppression tables, plus the body_text / template columns), do that first.`;
  }
  if (status === 403) return `${fallback}: this account isn't a super-admin.`;
  return serverMsg || err?.message || fallback;
}

// Prepend https:// to a bare host the user typed ("on-screen.app/about").
// Returns '' if it still doesn't look like a URL.
function normalizeUrl(value) {
  const s = String(value || '').trim();
  if (!s) return '';
  const withScheme = /^https?:\/\//i.test(s) ? s : `https://${s.replace(/^\/+/, '')}`;
  try { new URL(withScheme); return withScheme; } catch { return ''; }
}

// Split a free-form "To" string into email addresses.
function parseEmailList(text) {
  const seen = new Set();
  const emails = [];
  let invalid = 0;
  String(text || '').split(/[\s,;]+/).forEach((tok) => {
    const e = tok.trim().toLowerCase();
    if (!e) return;
    if (!EMAIL_RE.test(e)) { invalid += 1; return; }
    if (seen.has(e)) return;
    seen.add(e);
    emails.push(e);
  });
  return { emails, invalid };
}

// Parse pasted / uploaded prospect rows: "email, First Name, Organization".
// Tolerates a header row, quoted fields, tabs or commas, and blank lines.
function parseRecipients(text) {
  const rows = [];
  const seen = new Set();
  let invalid = 0;
  let duplicate = 0;

  String(text || '').split(/\r?\n/).forEach((line, idx) => {
    const trimmed = line.trim();
    if (!trimmed) return;
    const cells = trimmed
      .split(/\t|,/)
      .map(c => c.trim().replace(/^["']|["']$/g, '').trim());
    const email = (cells[0] || '').toLowerCase();
    // Skip an obvious header row.
    if (idx === 0 && !EMAIL_RE.test(email)) return;
    if (!EMAIL_RE.test(email)) { invalid += 1; return; }
    if (seen.has(email)) { duplicate += 1; return; }
    seen.add(email);
    rows.push({
      email,
      firstName: cells[1] || '',
      organizationName: cells[2] || '',
    });
  });

  return { rows, invalid, duplicate };
}

function ContactTab() {
  const [section, setSection] = useState('compose'); // 'compose' | 'campaigns'
  const [templates, setTemplates] = useState(null);

  useEffect(() => {
    getJson(`${OUTREACH_BASE}/templates`)
      .then(d => setTemplates(d.templates || []))
      .catch(() => setTemplates([]));
  }, []);

  return (
    <div className="sa-contact">
      <div className="sa-subtabs">
        <button className={`sa-subtab ${section === 'compose' ? 'active' : ''}`} onClick={() => setSection('compose')}>Compose</button>
        <button className={`sa-subtab ${section === 'campaigns' ? 'active' : ''}`} onClick={() => setSection('campaigns')}>Campaigns</button>
      </div>
      {section === 'compose' && <ComposeView templates={templates} />}
      {section === 'campaigns' && <CampaignsSection templates={templates} />}
    </div>
  );
}

function CampaignsSection({ templates }) {
  const [view, setView] = useState('list'); // 'list' | 'new' | 'detail'
  const [detailId, setDetailId] = useState(null);

  return (
    <>
      {view === 'list' && (
        <OutreachList
          onNew={() => setView('new')}
          onOpen={(id) => { setDetailId(id); setView('detail'); }}
        />
      )}
      {view === 'new' && (
        <OutreachNew
          templates={templates}
          onCancel={() => setView('list')}
          onCreated={(id) => { setDetailId(id); setView('detail'); }}
        />
      )}
      {view === 'detail' && detailId && (
        <OutreachDetail id={detailId} onBack={() => setView('list')} />
      )}
    </>
  );
}

// Shared: template picker + message editor used by Compose and New Campaign.
function TemplatePicker({ templates, value, onChange }) {
  if (!templates) return <div className="sa-muted" style={{ fontSize: '0.8rem' }}>Loading templates…</div>;
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {templates.map(t => (
        <label key={t.key} className={`sa-choice ${value === t.key ? 'active' : ''}`}>
          <input type="radio" name="tpl" checked={value === t.key} onChange={() => onChange(t)} />
          <span><strong>{t.label}</strong><br /><span className="sa-muted">{t.description}</span></span>
        </label>
      ))}
    </div>
  );
}

function ComposeView({ templates }) {
  const [tplKey, setTplKey] = useState('support');
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [headline, setHeadline] = useState('');
  const [ctaUrl, setCtaUrl] = useState('');
  const [ctaLabel, setCtaLabel] = useState('');
  const [body, setBody] = useState('');
  const [bodyTouched, setBodyTouched] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [busyKind, setBusyKind] = useState(null); // 'preview' | 'send'
  const [msg, setMsg] = useState(null);
  const [note, setNote] = useState(null);

  const tpl = (templates || []).find(t => t.key === tplKey);
  const busy = busyKind !== null;
  const { emails, invalid } = parseEmailList(to);
  const canSend = emails.length > 0 && subject.trim() && body.trim() && !busy;

  // When templates first load, seed the body with the current template's
  // default (unless the user has already typed something).
  useEffect(() => {
    if (tpl && !bodyTouched && !body) setBody(tpl.defaultBody || '');
  }, [tpl, bodyTouched, body]);

  // Switching template starts the message fresh from that template's default.
  const pickTemplate = (t) => {
    setTplKey(t.key);
    setBody(t.defaultBody || '');
    setBodyTouched(false);
  };

  const doPreview = async () => {
    setMsg(null); setBusyKind('preview');
    try {
      const d = await postJson(`${OUTREACH_BASE}/preview`, {
        template: tplKey, subject, body, headline, ctaUrl, ctaLabel,
      });
      setPreviewHtml(d.html || '');
    } catch (err) { setMsg(describeError(err, 'Preview failed')); }
    finally { setBusyKind(null); }
  };

  const doSend = async () => {
    setMsg(null); setNote(null);
    if (!canSend) { setMsg('Add a recipient, a subject, and a message.'); return; }
    if (!window.confirm(`Send to ${emails.length} recipient${emails.length === 1 ? '' : 's'} from info@on-screen.app now?`)) return;
    setBusyKind('send');
    try {
      const r = await postJson(`${OUTREACH_BASE}/compose`, {
        to: emails.join(', '), subject, body, template: tplKey, headline, ctaUrl, ctaLabel,
      });
      setNote(`Sent ${r.result.sent}${r.result.failed ? `, ${r.result.failed} failed` : ''}. Tracked under Campaigns as “${`Compose · ${subject}`.slice(0, 40)}”.`);
      setTo(''); setSubject(''); setBodyTouched(false); setPreviewHtml('');
    } catch (err) { setMsg(describeError(err, 'Send failed')); }
    finally { setBusyKind(null); }
  };

  return (
    <div>
      <div className="sa-panel">
        <h4>Compose a one-off email</h4>
        <p className="sa-muted" style={{ margin: 0 }}>
          Sent from <code>info@on-screen.app</code> immediately. For a support reply, use the
          recipient’s address. All three templates share the On-Screen header and footer —
          the template decides whether a headline, feature list, or button appears.
        </p>
      </div>

      {msg && <div className="sa-note error">{msg}</div>}
      {note && <div className="sa-note ok">{note}</div>}

      <div className="sa-broadcast-form">
        <div className="form-group">
          <label>Template</label>
          <TemplatePicker templates={templates} value={tplKey} onChange={pickTemplate} />
        </div>
        <div className="form-group">
          <label>To <span className="sa-muted">(one or more emails, comma or space separated)</span></label>
          <input type="text" value={to} onChange={e => setTo(e.target.value)} placeholder="jane@example.com, john@example.com" />
          <div style={{ marginTop: 4, fontSize: '0.8rem' }}>
            <span className={emails.length ? 'sa-count-ok' : 'sa-muted'}>{emails.length} recipient{emails.length === 1 ? '' : 's'}</span>
            {invalid > 0 && <span className="sa-count-bad"> · {invalid} not a valid email</span>}
          </div>
        </div>
        <div className="form-group">
          <label>Subject</label>
          <input type="text" value={subject} onChange={e => setSubject(e.target.value)} />
        </div>
        {tpl?.hasHeadlineField && (
          <div className="form-group">
            <label>Headline <span className="sa-muted">(optional — big navy text above your message)</span></label>
            <input type="text" value={headline} onChange={e => setHeadline(e.target.value)} />
          </div>
        )}
        {tpl?.hasCtaField && (
          <div className="form-group">
            <label>Button <span className="sa-muted">(optional)</span></label>
            <div className="sa-field-row">
              <input type="text" value={ctaLabel} onChange={e => setCtaLabel(e.target.value)} placeholder="Button text" />
              <input type="text" value={ctaUrl} placeholder="on-screen.app/…"
                onChange={e => setCtaUrl(e.target.value)}
                onBlur={e => { const n = normalizeUrl(e.target.value); if (n) setCtaUrl(n); }} />
            </div>
            {ctaUrl && !normalizeUrl(ctaUrl) && <div className="sa-count-bad" style={{ fontSize: '0.78rem', marginTop: 4 }}>That doesn’t look like a valid link.</div>}
          </div>
        )}
        <div className="form-group">
          <label>Message</label>
          <p className="sa-muted" style={{ margin: '0 0 6px', fontSize: '0.8rem' }}>
            Shown after “Hi {'{first_name}'},” (falls back to “Hi there,”). Blank line = new paragraph.
          </p>
          <textarea className="sa-mono" rows={9} value={body}
            onChange={e => { setBody(e.target.value); setBodyTouched(true); }} />
        </div>

        <div className="sa-btn-row">
          <button className="sa-btn" onClick={doPreview} disabled={busy}>
            {busyKind === 'preview' ? 'Loading…' : 'Preview'}
          </button>
          <button className="sa-btn primary" onClick={doSend} disabled={!canSend}>
            {busyKind === 'send' ? 'Sending…' : `Send now${emails.length ? ` (${emails.length})` : ''}`}
          </button>
        </div>
      </div>

      {previewHtml && (
        <div className="sa-panel">
          <h4>Preview</h4>
          <iframe title="Email preview" srcDoc={previewHtml}
            style={{ width: '100%', height: 620, border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff' }} />
        </div>
      )}
    </div>
  );
}

function OutreachList({ onNew, onOpen }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSuppression, setShowSuppression] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    getJson(`${OUTREACH_BASE}/campaigns`)
      .then(d => setCampaigns(d.campaigns || []))
      .catch(() => setCampaigns([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="sa-section-header">
        <h3>Outreach Campaigns</h3>
        <button className="sa-btn primary" onClick={onNew}>New Campaign</button>
      </div>

      {loading ? (
        <div className="sa-panel">Loading…</div>
      ) : campaigns.length === 0 ? (
        <div className="sa-panel">No campaigns yet. Click <strong>New Campaign</strong> to create one.</div>
      ) : (
        <div className="sa-table-wrap" style={{ overflowX: 'auto' }}>
          <table className="sa-table">
            <thead>
              <tr>
                <th>Name</th><th>Status</th><th>Recipients</th>
                <th>Sent</th><th>Opened</th><th>Clicked</th><th>Unsub</th><th>Created</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map(c => (
                <tr key={c.id} className="clickable" onClick={() => onOpen(c.id)}>
                  <td>{c.name}</td>
                  <td><span className={`sa-badge ${c.status === 'sent' ? 'resolved' : c.status === 'sending' ? 'active' : ''}`}>{c.status}</span></td>
                  <td>{c.stats.recipients}</td>
                  <td>{c.stats.sent}</td>
                  <td>{c.stats.opened}</td>
                  <td>{c.stats.clicked}</td>
                  <td>{c.stats.unsubscribed}</td>
                  <td>{new Date(c.createdAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="sa-btn-row" style={{ marginTop: 8 }}>
        <button className="sa-btn" onClick={() => setShowSuppression(v => !v)}>
          {showSuppression ? 'Hide' : 'Show'} do-not-contact list
        </button>
      </div>
      {showSuppression && <SuppressionManager />}
    </div>
  );
}

function SuppressionManager() {
  const [entries, setEntries] = useState([]);
  const [email, setEmail] = useState('');
  const [msg, setMsg] = useState(null);

  const load = useCallback(() => {
    getJson(`${OUTREACH_BASE}/suppression`).then(d => setEntries(d.entries || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    setMsg(null);
    try {
      await postJson(`${OUTREACH_BASE}/suppression`, { email: email.trim() });
      setEmail('');
      load();
    } catch (err) {
      setMsg(err.message || 'Failed to add');
    }
  };
  const remove = async (e) => {
    try { await delJson(`${OUTREACH_BASE}/suppression/${encodeURIComponent(e)}`); load(); } catch { /* noop */ }
  };

  return (
    <div className="sa-broadcast-form" style={{ marginTop: 12 }}>
      <div className="form-group">
        <label>Do-not-contact list ({entries.length})</label>
        <p className="sa-muted" style={{ margin: '0 0 6px', fontSize: '0.8rem' }}>
          These addresses are skipped on every send. Unsubscribes land here automatically.
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input type="text" value={email} placeholder="email@example.com"
            style={{ flex: '1 1 180px' }}
            onChange={e => setEmail(e.target.value)} />
          <button className="sa-btn" onClick={add} disabled={!EMAIL_RE.test(email.trim())}>Add</button>
        </div>
        {msg && <div className="sa-count-bad" style={{ fontSize: '0.8rem', marginTop: 4 }}>{msg}</div>}
      </div>
      {entries.length > 0 && (
        <div className="sa-table-wrap" style={{ maxHeight: 260, overflow: 'auto' }}>
          <table className="sa-table">
            <thead><tr><th>Email</th><th>Reason</th><th>Added</th><th /></tr></thead>
            <tbody>
              {entries.map(en => (
                <tr key={en.email}>
                  <td>{en.email}</td>
                  <td>{en.reason}</td>
                  <td>{new Date(en.created_at).toLocaleDateString()}</td>
                  <td><button className="sa-btn danger" onClick={() => remove(en.email)}>Remove</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const EXAMPLE_RECIPIENTS = [
  'jane@riversideskate.com, Jane, Riverside Skate Shop',
  'coach.mike@northendboosters.org, Mike, North End Booster Club',
  'events@maplewoodpta.org, Dana, Maplewood PTA',
].join('\n');

const FALLBACK_BODY =
  "I'm reaching out from On-Screen — one platform for {organization_name} to " +
  'schedule content, run any event (ticketing, auctions, raffles), streamline ' +
  'your workflow, and keep your audience coming back, all in one place.\n\n' +
  'Instead of juggling separate tools for outreach, ticketing, and event-day ' +
  'logistics, your team and audience get one clean, branded experience — ' +
  'before, during, and after every event.';

function OutreachNew({ templates, onCancel, onCreated }) {
  const [tplKey, setTplKey] = useState('outreach');
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [fromName, setFromName] = useState('The On-Screen Team');
  const [ctaUrl, setCtaUrl] = useState('https://on-screen.app/about');
  const [ctaLabel, setCtaLabel] = useState('');
  const [headline, setHeadline] = useState('');
  const [previewText, setPreviewText] = useState('');
  const [body, setBody] = useState(FALLBACK_BODY);
  const [bodyTouched, setBodyTouched] = useState(false);
  const [raw, setRaw] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [busyKind, setBusyKind] = useState(null); // 'preview' | 'create'
  const [msg, setMsg] = useState(null);

  const tpl = (templates || []).find(t => t.key === tplKey);

  // Seed the message from the template's default once templates arrive
  // (unless the user has already edited it).
  useEffect(() => {
    if (tpl && !bodyTouched) setBody(tpl.defaultBody || '');
  }, [tpl, bodyTouched]);

  const pickTemplate = (t) => { setTplKey(t.key); setBody(t.defaultBody || ''); setBodyTouched(false); };

  const parsed = parseRecipients(raw);
  const busy = busyKind !== null;
  const canCreate = name.trim() && subject.trim() && parsed.rows.length > 0 && !busy;

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setRaw(prev => (prev ? prev.replace(/\s*$/, '') + '\n' : '') + String(reader.result || ''));
    reader.readAsText(file);
    e.target.value = '';
  };

  const fillExample = () => {
    setName(prev => prev || 'Example — local orgs');
    setSubject(prev => prev || 'A simpler way to run your events');
    setRaw(prev => (prev.trim() ? prev : EXAMPLE_RECIPIENTS));
  };

  const commonFields = () => ({
    template: tplKey, fromName, previewText, subject, body,
    headline: tpl?.hasHeadlineField ? headline : undefined,
    ctaLabel: tpl?.hasCtaField ? ctaLabel : undefined,
    ctaUrl,
  });

  const doPreview = async () => {
    setMsg(null); setBusyKind('preview');
    try {
      const d = await postJson(`${OUTREACH_BASE}/preview`, {
        ...commonFields(),
        sample: parsed.rows[0] || undefined,
      });
      setPreviewHtml(d.html || '');
    } catch (err) {
      setMsg(describeError(err, 'Preview failed'));
    } finally {
      setBusyKind(null);
    }
  };

  const doCreate = async () => {
    setMsg(null);
    if (!name.trim() || !subject.trim()) { setMsg('Give the campaign a name and a subject line.'); return; }
    if (parsed.rows.length === 0) { setMsg('Add at least one recipient — one email per line in the box below.'); return; }
    setBusyKind('create');
    try {
      const d = await postJson(`${OUTREACH_BASE}/campaigns`, {
        ...commonFields(),
        name: name.trim(), subject: subject.trim(),
        recipients: parsed.rows,
      });
      onCreated(d.campaignId);
    } catch (err) {
      setMsg(describeError(err, 'Could not save the campaign'));
      setBusyKind(null);
    }
  };

  return (
    <div>
      <div className="sa-section-header">
        <h3>New Campaign</h3>
        <button className="sa-btn" onClick={onCancel}>Cancel</button>
      </div>

      <div className="sa-panel">
        <h4>How this works</h4>
        <ol>
          <li>Pick a template, fill in the fields, edit the message, paste your recipient list.</li>
          <li><strong>Preview</strong> to see the exact email.</li>
          <li><strong>Save</strong> — nothing is sent yet.</li>
          <li>On the next screen: <strong>Send now</strong> (small lists) or <strong>Queue send</strong> (background worker).</li>
        </ol>
        <p className="sa-muted" style={{ margin: '8px 0 0' }}>
          Sent from <code>info@on-screen.app</code>. Every recipient gets tracked open/click/unsubscribe
          links. For a quick one-off with no list or tracking, use <strong>Compose</strong> instead.
        </p>
      </div>

      {msg && <div className="sa-note error">{msg}</div>}

      <div className="sa-broadcast-form">
        <div className="form-group">
          <label>Template</label>
          <TemplatePicker templates={templates} value={tplKey} onChange={pickTemplate} />
        </div>
        <div className="form-group">
          <label>Campaign name</label>
          <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Skate shops — March (only you see this)" />
        </div>
        <div className="form-group">
          <label>Subject line</label>
          <input type="text" value={subject} onChange={e => setSubject(e.target.value)} placeholder="e.g. A simpler way to run your events" />
        </div>
        <div className="form-group">
          <label>From name</label>
          <div className="sa-field-row">
            <input type="text" value={fromName} onChange={e => setFromName(e.target.value)} placeholder="Sender name" />
            <input type="text" value="info@on-screen.app" readOnly title="Sending address is fixed" />
          </div>
        </div>
        {tpl?.hasHeadlineField && (
          <div className="form-group">
            <label>Headline <span className="sa-muted">(optional — navy text above the message)</span></label>
            <input type="text" value={headline} onChange={e => setHeadline(e.target.value)} />
          </div>
        )}
        <div className="form-group">
          <label>Button link {tpl?.fixedCta && <span className="sa-muted">(where the CTA button goes)</span>}</label>
          <div className={tpl?.hasCtaField ? 'sa-field-row' : ''}>
            {tpl?.hasCtaField && (
              <input type="text" value={ctaLabel} onChange={e => setCtaLabel(e.target.value)} placeholder="Button text (blank = no button)" />
            )}
            <input type="text" value={ctaUrl} placeholder="on-screen.app/about"
              onChange={e => setCtaUrl(e.target.value)}
              onBlur={e => { const n = normalizeUrl(e.target.value); if (n) setCtaUrl(n); }} />
          </div>
          {ctaUrl && !normalizeUrl(ctaUrl) && <div className="sa-count-bad" style={{ fontSize: '0.78rem', marginTop: 4 }}>That doesn’t look like a valid link.</div>}
        </div>
        <div className="form-group">
          <label>Inbox preview text <span className="sa-muted">(optional)</span></label>
          <input type="text" value={previewText} onChange={e => setPreviewText(e.target.value)} placeholder="The grey line shown after the subject in most inboxes" />
        </div>

        <div className="form-group">
          <label>Message</label>
          <p className="sa-muted" style={{ margin: '0 0 6px', fontSize: '0.8rem' }}>
            Shown right after “Hi {'{first_name}'},”. Blank line = new paragraph.
            Placeholders: <code>{'{first_name}'}</code> <code>{'{organization_name}'}</code> (fall back to
            “there” / “your organization” when a recipient has none).
          </p>
          <textarea className="sa-mono" value={body} onChange={e => { setBody(e.target.value); setBodyTouched(true); }} rows={8} />
          <div className="sa-btn-row">
            <button type="button" className="sa-btn" onClick={() => { setBody(tpl?.defaultBody || FALLBACK_BODY); setBodyTouched(false); }}>Reset to template default</button>
          </div>
        </div>

        <div className="form-group">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <label style={{ margin: 0 }}>Recipients</label>
            <button type="button" className="sa-btn" style={{ padding: '2px 8px', fontSize: '0.75rem' }} onClick={fillExample}>
              Fill example
            </button>
          </div>
          <p className="sa-muted" style={{ margin: '4px 0 6px', fontSize: '0.8rem' }}>
            One per line. Just an email works, or add name + organization after commas:
            <br /><code>jane@shop.com, Jane, Riverside Skate</code>
          </p>
          <textarea className="sa-mono" value={raw} onChange={e => setRaw(e.target.value)} rows={6}
            placeholder={EXAMPLE_RECIPIENTS} />
          <div style={{ marginTop: 6, fontSize: '0.82rem' }}>
            <span className={parsed.rows.length ? 'sa-count-ok' : 'sa-muted'}>
              {parsed.rows.length} valid recipient{parsed.rows.length === 1 ? '' : 's'}
            </span>
            {parsed.invalid > 0 && <span className="sa-count-bad"> · {parsed.invalid} skipped (not an email)</span>}
            {parsed.duplicate > 0 && <span className="sa-count-warn"> · {parsed.duplicate} duplicate</span>}
          </div>
          <div style={{ marginTop: 8, fontSize: '0.8rem' }} className="sa-muted">
            or upload a CSV / text file: <input type="file" accept=".csv,.txt" onChange={onFile} />
          </div>
        </div>

        {parsed.rows.length > 0 && (
          <div className="sa-table-wrap" style={{ maxHeight: 200, overflow: 'auto' }}>
            <table className="sa-table">
              <thead><tr><th>Email</th><th>First name</th><th>Organization</th></tr></thead>
              <tbody>
                {parsed.rows.slice(0, 50).map(r => (
                  <tr key={r.email}>
                    <td>{r.email}</td><td>{r.firstName || '—'}</td><td>{r.organizationName || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="sa-btn-row">
          <button className="sa-btn" onClick={doPreview} disabled={busy}>
            {busyKind === 'preview' ? 'Loading…' : 'Preview email'}
          </button>
          <button className="sa-btn primary" onClick={doCreate} disabled={!canCreate}
            title={canCreate ? '' : 'Add a name, subject, and at least one recipient first'}>
            {busyKind === 'create' ? 'Saving…' : 'Save campaign →'}
          </button>
        </div>
      </div>

      {previewHtml && (
        <div className="sa-panel">
          <h4>Preview</h4>
          <iframe title="Email preview" srcDoc={previewHtml}
            style={{ width: '100%', height: 640, border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff' }} />
        </div>
      )}
    </div>
  );
}

function OutreachDetail({ id, onBack }) {
  const [data, setData] = useState(null);
  const [busyKind, setBusyKind] = useState(null); // 'now' | 'queue' | 'pause' | 'save'
  const [msg, setMsg] = useState(null);
  const [note, setNote] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);

  const load = useCallback(() => {
    getJson(`${OUTREACH_BASE}/campaigns/${id}`).then(setData).catch(() => setData(null));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Poll while a send is in progress.
  useEffect(() => {
    if (data?.campaign?.status !== 'sending') return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [data?.campaign?.status, load]);

  if (!data) return <div className="sa-panel">Loading…</div>;

  const { campaign, stats, recipients } = data;
  const busy = busyKind !== null;

  const pending = (stats.recipients || 0) - (stats.sent || 0) - (stats.failed || 0)
    - (stats.skipped || 0) - (stats.unsubscribed || 0);

  const doSend = async (now) => {
    const how = now ? 'Send now' : 'Queue for the background worker';
    if (!window.confirm(`${how}: ${pending} email${pending === 1 ? '' : 's'} from info@on-screen.app?`)) return;
    setBusyKind(now ? 'now' : 'queue'); setMsg(null); setNote(null);
    try {
      const r = await postJson(`${OUTREACH_BASE}/campaigns/${id}/send`, { now });
      if (r.status === 'now') {
        setNote(`Sent ${r.result.sent}. ${r.result.failed ? `${r.result.failed} failed. ` : ''}${r.result.remaining ? `${r.result.remaining} still queued.` : ''}`);
      } else {
        setNote(`Queued ${r.pending}. The background worker will send them.`);
      }
      load();
    } catch (err) {
      setMsg(describeError(err, 'Send failed'));
    } finally {
      setBusyKind(null);
    }
  };

  const pause = async () => {
    setBusyKind('pause');
    try { await postJson(`${OUTREACH_BASE}/campaigns/${id}/pause`, {}); load(); }
    catch { /* noop */ }
    finally { setBusyKind(null); }
  };

  const startEdit = () => {
    setDraft({
      name: campaign.name, subject: campaign.subject, fromName: campaign.fromName,
      ctaUrl: campaign.ctaUrl, ctaLabel: campaign.ctaLabel || '',
      previewText: campaign.previewText || '', body: campaign.bodyText || '',
      headline: campaign.headline || '',
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    setBusyKind('save'); setMsg(null);
    try {
      await patchJson(`${OUTREACH_BASE}/campaigns/${id}`, draft);
      setEditing(false);
      load();
    } catch (err) {
      setMsg(describeError(err, 'Could not save changes'));
    } finally {
      setBusyKind(null);
    }
  };

  const statusBadge = (
    <span className={`sa-badge ${campaign.status === 'sent' ? 'resolved' : campaign.status === 'sending' ? 'active' : ''}`}>
      {campaign.status}
    </span>
  );

  return (
    <div>
      <div className="sa-section-header">
        <h3>{campaign.name}</h3>
        <button className="sa-btn" onClick={onBack}>Back</button>
      </div>

      {msg && <div className="sa-note error">{msg}</div>}
      {note && <div className="sa-note ok">{note}</div>}

      <div className="sa-metrics-grid" style={{ marginBottom: 14 }}>
        {[
          ['Recipients', stats.recipients], ['Sent', stats.sent], ['Opened', stats.opened],
          ['Clicked', stats.clicked], ['Unsubscribed', stats.unsubscribed], ['Failed', stats.failed],
        ].map(([label, value]) => (
          <div className="sa-metric-card" key={label}>
            <div className="value">{value ?? 0}</div>
            <div className="label">{label}</div>
          </div>
        ))}
      </div>

      <div className="sa-panel">
        <div style={{ marginBottom: 8 }}>
          {statusBadge} &nbsp;<span className="sa-muted">{pending} not sent yet · {campaign.template} template</span>
        </div>
        <div><strong>Subject:</strong> {campaign.subject}</div>
        <div style={{ wordBreak: 'break-all' }}><strong>Button link:</strong> {campaign.ctaUrl}</div>
      </div>

      {campaign.status === 'draft' && !editing && (
        <div className="sa-note info">Draft — <strong>nothing has been sent</strong>. Edit the text if you need to, then choose a send option below.</div>
      )}
      {campaign.status === 'sending' && (
        <div className="sa-note warn">
          Sending… {stats.sent}/{stats.recipients} done — this page refreshes itself.
          {stats.sent === 0 && (
            <><br />Still 0 after a minute? The background worker isn’t running:
            <br /><code>pm2 start ecosystem.config.js --only outreach-sender</code>
            <br />or use <strong>Send now</strong> below.</>
          )}
        </div>
      )}
      {campaign.status === 'sent' && (
        <div className="sa-note ok">Campaign complete — {stats.sent} sent.</div>
      )}

      {editing ? (
        <div className="sa-broadcast-form">
          <div className="form-group"><label>Campaign name</label>
            <input type="text" value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} /></div>
          <div className="form-group"><label>Subject line</label>
            <input type="text" value={draft.subject} onChange={e => setDraft({ ...draft, subject: e.target.value })} /></div>
          <div className="form-group"><label>From name</label>
            <input type="text" value={draft.fromName} onChange={e => setDraft({ ...draft, fromName: e.target.value })} /></div>
          {campaign.template === 'general' && (
            <div className="form-group"><label>Headline <span className="sa-muted">(optional)</span></label>
              <input type="text" value={draft.headline} onChange={e => setDraft({ ...draft, headline: e.target.value })} /></div>
          )}
          <div className="form-group"><label>Button link</label>
            <div className={campaign.template === 'general' ? 'sa-field-row' : ''}>
              {campaign.template === 'general' && (
                <input type="text" value={draft.ctaLabel} placeholder="Button text (blank = no button)"
                  onChange={e => setDraft({ ...draft, ctaLabel: e.target.value })} />
              )}
              <input type="text" value={draft.ctaUrl} onChange={e => setDraft({ ...draft, ctaUrl: e.target.value })} />
            </div>
          </div>
          <div className="form-group"><label>Inbox preview text</label>
            <input type="text" value={draft.previewText} onChange={e => setDraft({ ...draft, previewText: e.target.value })} /></div>
          <div className="form-group"><label>Message</label>
            <textarea className="sa-mono" rows={8} value={draft.body} onChange={e => setDraft({ ...draft, body: e.target.value })} /></div>
          <div className="sa-btn-row">
            <button className="sa-btn" onClick={() => setEditing(false)} disabled={busy}>Cancel</button>
            <button className="sa-btn primary" onClick={saveEdit} disabled={busy}>
              {busyKind === 'save' ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      ) : (
        <div className="sa-btn-row" style={{ marginBottom: 14 }}>
          {campaign.status === 'draft' && (
            <button className="sa-btn" onClick={startEdit} disabled={busy}>Edit text</button>
          )}
          {['draft', 'paused', 'sending'].includes(campaign.status) && pending > 0 && (
            <>
              <button className="sa-btn primary" onClick={() => doSend(true)} disabled={busy}>
                {busyKind === 'now' ? 'Sending…' : `Send now (${pending})`}
              </button>
              <button className="sa-btn" onClick={() => doSend(false)} disabled={busy}>
                {busyKind === 'queue' ? 'Queuing…' : 'Queue send'}
              </button>
            </>
          )}
          {campaign.status === 'sending' && (
            <button className="sa-btn" onClick={pause} disabled={busy}>Pause</button>
          )}
        </div>
      )}

      <div className="sa-table-wrap" style={{ maxHeight: 460, overflow: 'auto' }}>
        <table className="sa-table">
          <thead>
            <tr><th>Email</th><th>Name</th><th>Org</th><th>Status</th><th>Opens</th><th>Clicks</th></tr>
          </thead>
          <tbody>
            {recipients.map(r => (
              <tr key={r.id}>
                <td>{r.email}</td>
                <td>{r.firstName || '—'}</td>
                <td>{r.organizationName || '—'}</td>
                <td>
                  <span className={`sa-badge ${r.status === 'sent' ? 'resolved' : r.status === 'failed' ? 'disabled' : ''}`}>{r.status}</span>
                  {r.error && <span title={r.error} className="sa-count-bad" style={{ marginLeft: 4 }}>⚠</span>}
                </td>
                <td>{r.openCount}</td>
                <td>{r.clickCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
